/**
 * NivNotesThread — reusable newest-first notes/updates thread with a composer.
 *
 * Props:
 *  - notes: [{ id, text, createdAt, author }]
 *  - onAdd(text): async — parent persists; composer clears on success
 *  - isAdding: disables the composer while saving
 *  - emptyLabel: Hebrew empty-state text
 */

import { useState } from "react";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import "./NivNotesThread.css";

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function NivNotesThread({ notes = [], onAdd, isAdding = false, emptyLabel = "אין עדכונים עדיין" }) {
  const [draft, setDraft] = useState("");
  const sorted = [...notes].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );

  const handleSave = async () => {
    const text = draft.trim();
    if (!text || isAdding) return;
    const ok = await onAdd(text);
    if (ok !== false) setDraft("");
  };

  return (
    <div className="nivnt-thread">
      <div className="nivnt-composer">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="הוסף עדכון..."
          rows={2}
          disabled={isAdding}
        />
        <button
          type="button"
          className="nivnt-save-btn"
          onClick={handleSave}
          disabled={isAdding || !draft.trim()}
        >
          <PaperPlaneTilt size={14} />
          {isAdding ? "שומר…" : "שמור"}
        </button>
      </div>

      <div className="nivnt-list">
        {sorted.length === 0 && <div className="nivnt-empty">{emptyLabel}</div>}
        {sorted.map((note) => (
          <div key={note.id} className="nivnt-item">
            <p className="nivnt-text">{note.text}</p>
            <div className="nivnt-meta">
              <span>{note.author || "—"}</span>
              <span dir="ltr">{formatDateTime(note.createdAt)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default NivNotesThread;
